import { useEffect } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { theme } from '@/constants/theme';

type AnimatedListProps<T> = {
  items: T[];
  onSelect?: (item: T) => void;
  renderItem?: (item: T, index: number) => React.ReactNode;
  staggerMs?: number;
};

function ListRow<T>({
  item,
  index,
  delay,
  onSelect,
  renderItem,
}: {
  item: T;
  index: number;
  delay: number;
  onSelect?: (item: T) => void;
  renderItem?: (item: T, index: number) => React.ReactNode;
}) {
  const mountX = useSharedValue(24);
  const mountOpacity = useSharedValue(0);
  const pressScale = useSharedValue(1);
  const hoverBg = useSharedValue(0);

  useEffect(() => {
    mountOpacity.value = withDelay(
      delay,
      withTiming(1, { duration: 380, easing: Easing.out(Easing.cubic) }),
    );
    mountX.value = withDelay(
      delay,
      withSpring(0, { damping: 18, stiffness: 140 }),
    );
  }, [delay]);

  const rowStyle = useAnimatedStyle(() => ({
    opacity: mountOpacity.value,
    transform: [
      { translateX: mountX.value },
      { scale: pressScale.value },
    ],
    ...(Platform.OS === 'web'
      ? ({ backgroundColor: `rgba(30, 38, 70, ${0.6 + hoverBg.value * 0.4})` } as any)
      : {}),
  }));

  return (
    <Pressable
      onPress={() => onSelect?.(item)}
      onHoverIn={() => {
        hoverBg.value = withTiming(1, { duration: 180 });
      }}
      onHoverOut={() => {
        hoverBg.value = withTiming(0, { duration: 260 });
      }}
      onPressIn={() => {
        pressScale.value = withSpring(0.97, { damping: 16, stiffness: 300 });
      }}
      onPressOut={() => {
        pressScale.value = withSpring(1, { damping: 14, stiffness: 220 });
      }}
    >
      <Animated.View style={[s.item, rowStyle]}>
        {renderItem ? (
          renderItem(item, index)
        ) : (
          <Text style={s.itemText}>{String(item)}</Text>
        )}
      </Animated.View>
    </Pressable>
  );
}

export default function AnimatedList<T>({
  items,
  onSelect,
  renderItem,
  staggerMs = 60,
}: AnimatedListProps<T>) {
  return (
    <View style={s.list}>
      {items.map((item, index) => (
        <ListRow
          key={index}
          item={item}
          index={index}
          delay={index * staggerMs}
          onSelect={onSelect}
          renderItem={renderItem}
        />
      ))}
    </View>
  );
}

const s = StyleSheet.create({
  list: { gap: 10 },
  item: {
    backgroundColor: '#0f1326',
    borderWidth: 1,
    borderColor: '#2A315A',
    padding: 14,
    borderRadius: 12,
  },
  itemText: {
    color: '#E6E9FA',
    fontFamily: theme.fonts.medium,
    textAlign: 'right',
  },
});
